import {
  IonButton,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonPopover,
} from '@ionic/react';
import { ReactElement } from 'react';
import {
  GalleryImage,
  GalleryThumbnails,
} from '../../../../../../../components/image/gallery';
import i18n from '../../../../../../../i18n';

interface LegendSettingDetail {
  label: string;
  description: string;
  image?: string;
}

enum LegendSetting {
  START_OFFSET = 'startOffset',
  END_OFFSET = 'endOffset',
  PERPENDICULAR_TO_PATH = 'perpendicularToPath',
  TORCH_OFFSET = 'torchOffset',
  TRAVEL_ANGLE = 'travelAngle',
  WORK_ANGLE = 'workAngle',
  REVERSE_PATH = 'reversePath',
  WELD_LIBRARY = 'weldLibrary',
  POST_ACTION = 'postAction',
}

const LegendSettingDetails: Record<LegendSetting, LegendSettingDetail> = {
  [LegendSetting.START_OFFSET]: {
    label: i18n.t('welding:multipass_schedule_header_start_offset_abbr'),
    description: 'Shifts the weld pass start point along the weld seam (in mm)',
  },
  [LegendSetting.END_OFFSET]: {
    label: i18n.t('welding:multipass_schedule_header_end_offset_abbr'),
    description: 'Shifts the weld pass end point along the weld seam (in mm)',
  },
  [LegendSetting.PERPENDICULAR_TO_PATH]: {
    label: i18n.t(
      'welding:multipass_schedule_header_perpendicular_to_path_abbr'
    ),
    description:
      'Shift weld pass toward upper toe with positive values (in mm); negative for lower toe.',
  },
  [LegendSetting.TORCH_OFFSET]: {
    label: i18n.t('welding:multipass_schedule_header_torch_offset_abbr'),
    description:
      'Shift weld pass up along the wire axis with positive values (in mm); negative for down.',
  },
  [LegendSetting.TRAVEL_ANGLE]: {
    label: i18n.t('welding:multipass_schedule_header_travel_angle_abbr'),
    description:
      'Modifies the torch travel (push/pull) angle relative to the weld seam (in degrees)',
  },
  [LegendSetting.WORK_ANGLE]: {
    label: i18n.t('welding:multipass_schedule_header_work_angle_abbr'),
    description: 'Modifies the torch work angle (in degrees)',
  },
  [LegendSetting.REVERSE_PATH]: {
    label: i18n.t('welding:multipass_schedule_header_reverse_path_abbr'),
    description: 'The direction of the torch in relation to the path.',
  },
  [LegendSetting.WELD_LIBRARY]: {
    label: i18n.t('welding:weld_library'),
    description:
      'The weld settings that will be applied to each point in the pass.',
  },
  [LegendSetting.POST_ACTION]: {
    label: i18n.t('common:post_action'),
    description: "The action taken after the pass's depart item.",
  },
};

const legendImages: GalleryImage[] = [
  { src: '/assets/welding/multipass_offsets.jpg', alt: 'Multipass Offsets' },
  { src: '/assets/welding/multipass_angles.jpg', alt: 'Multipass Angles' },
];

const MultipassScheduleLegend = (): ReactElement => {
  return (
    <>
      <IonButton fill="clear" className="ion-no-padding" id="legend-trigger">
        {i18n.t('common:info')}
      </IonButton>
      <IonPopover reference="trigger" trigger="legend-trigger">
        <IonContent style={{ overflow: 'auto', maxHeight: '60vh' }}>
          <GalleryThumbnails
            images={legendImages}
            size={72}
            style={{
              margin: '0 auto',
              marginBottom: 16,
              width: '100%',
              maxWidth: 400,
            }}
          />
          <IonList>
            <LegendKey legendSetting={LegendSetting.START_OFFSET} />
            <LegendKey legendSetting={LegendSetting.END_OFFSET} />
            <LegendKey legendSetting={LegendSetting.PERPENDICULAR_TO_PATH} />
            <LegendKey legendSetting={LegendSetting.TORCH_OFFSET} />
            <LegendKey legendSetting={LegendSetting.TRAVEL_ANGLE} />
            <LegendKey legendSetting={LegendSetting.WORK_ANGLE} />
            <LegendKey legendSetting={LegendSetting.REVERSE_PATH} />
            <LegendKey legendSetting={LegendSetting.WELD_LIBRARY} />
            <LegendKey legendSetting={LegendSetting.POST_ACTION} />
          </IonList>
        </IonContent>
      </IonPopover>
    </>
  );
};

interface LegendKeyProps {
  legendSetting?: LegendSetting;
  onClick?: () => void;
}

const LegendKey = (props: LegendKeyProps): ReactElement => {
  const { legendSetting, onClick } = props;
  const { label, description, image } = LegendSettingDetails[legendSetting!];
  const hasImage = image !== undefined;

  return (
    <IonItem
      lines="none"
      className="legend-item"
      button={hasImage}
      onClick={hasImage ? onClick : undefined}
    >
      <IonLabel>
        <b>{label}:&nbsp;</b>
        {description}
      </IonLabel>
    </IonItem>
  );
};

export { MultipassScheduleLegend };
